/**
 * CARD HOLD — step 4: the chip, the job page panel, the red call rows and the
 * ⓘ "How card holds work" (#2037; mockup Hq93GUWq3pbxXLACELEYB9, moments 2,
 * 3 and 6; Corbin approved 10/24).
 *
 * Colors carry the meaning, and the words say the same thing so nobody has to
 * know the colors (visual first, 7th grade):
 *   green = the money is safe · red = call someone · gray = nothing is held ·
 *   blue = paid · amber = the hold goes on the day before.
 *
 * "Card won't hold — call the customer" lives in the booking chip's flyout
 * and at the top of Closeout. Never the bell, never a phone ping.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CreditCard, Loader2, MapPin, Phone, Send, Undo2, Unlock } from "lucide-react";
import { toast } from "sonner";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { HowItWorks, type HowStep } from "@/components/how-it-works";
import { confirmDialog } from "@/components/ui/app-dialogs";
import { cn } from "@/lib/utils";

export type HoldChipState =
    | "waiting"
    | "held"
    | "wont_hold"
    | "released"
    | "expired"
    | "charged"
    | "net30"
    | "wo_vendor"
    | "collect_on_site"
    | "no_card"
    | "no_price";

/** GET /api/jobs/:jobId/card-hold (src/server/services/cardHoldView.ts). */
export interface JobHoldView {
    show: boolean;
    switchOn: boolean;
    state: HoldChipState | null;
    amountCents: number | null;
    card: { brand: string | null; last4: string | null } | null;
    holdOn: string | null;
    visitDate: string | null;
    placedAt: string | null;
    endsAt: string | null;
    reason: string | null;
    releaseReason: string | null;
    capturedCents: number | null;
    extraCents: number | null;
    extraStatus: string | null;
    customerId: string | null;
    canRelease: boolean;
    canCollectOnSite: boolean;
    canSendLink: boolean;
}

/** GET /api/card-holds/alerts — one red row each. */
export interface CardHoldAlert {
    jobId: string;
    jobNumber: string | null;
    customerId: string;
    customerName: string;
    devices: number;
    visitDate: string;
    when: string;
    reason: string;
    amountCents: number;
    phone: string | null;
    email: string | null;
    triedAt: string | null;
    linkSentAt: string | null;
}

export const CARD_HOLD_ALERTS_KEY = ["card-holds", "alerts"] as const;

export function money(cents: number | null | undefined): string {
    return `$${((cents ?? 0) / 100).toFixed(2)}`;
}

/** "2026-10-26" → "Mon 10/26". */
export function dayLabel(date: string | null | undefined): string {
    if (!date) return "";
    const d = new Date(`${date}T12:00:00Z`);
    if (Number.isNaN(d.getTime())) return date;
    return new Intl.DateTimeFormat("en-US", { timeZone: "UTC", weekday: "short", month: "numeric", day: "numeric" })
        .format(d)
        .replace(",", "");
}

function timeLabel(at: string | null): string {
    if (!at) return "";
    const d = new Date(at);
    if (Number.isNaN(d.getTime())) return "";
    return new Intl.DateTimeFormat("en-US", {
        timeZone: "America/Chicago",
        weekday: "short",
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
    })
        .format(d)
        .replace(",", "");
}

/** An instant → "Mon 10/26" on the Central calendar. */
function dayOf(at: string | null): string {
    if (!at) return "";
    const d = new Date(at);
    if (Number.isNaN(d.getTime())) return "";
    return new Intl.DateTimeFormat("en-US", {
        timeZone: "America/Chicago",
        weekday: "short",
        month: "numeric",
        day: "numeric",
    })
        .format(d)
        .replace(",", "");
}

function cardWords(card: JobHoldView["card"]): string {
    if (!card) return "the card on file";
    const brand = card.brand ? card.brand[0]!.toUpperCase() + card.brand.slice(1) : "Card";
    return card.last4 ? `${brand} ••${card.last4}` : brand;
}

type Tone = "green" | "red" | "gray" | "blue" | "amber";

export const CHIP_TONE: Record<HoldChipState, Tone> = {
    waiting: "amber",
    held: "green",
    wont_hold: "red",
    released: "gray",
    expired: "gray",
    charged: "blue",
    net30: "gray",
    wo_vendor: "gray",
    collect_on_site: "gray",
    no_card: "gray",
    no_price: "gray",
};

/** The chip's words. Pure, so the copy is testable. */
export function holdChipLabel(state: HoldChipState, amountCents: number | null, holdOn?: string | null): string {
    switch (state) {
        case "waiting":
            return holdOn ? `Hold ${dayLabel(holdOn)}` : "Hold the day before";
        case "held":
            return amountCents ? `Held ${money(amountCents)}` : "Held";
        case "wont_hold":
            return "Won't hold";
        case "released":
            return "Released";
        case "expired":
            return "Hold ran out";
        case "charged":
            return amountCents ? `Charged ${money(amountCents)}` : "Charged";
        case "net30":
            return "No hold: Net 30";
        case "wo_vendor":
            return "No hold: work order";
        case "collect_on_site":
            return "No hold: collect on site";
        case "no_card":
            return "No hold: no card";
        case "no_price":
            return "No hold: no price yet";
    }
}

const TONE_CLASS: Record<Tone, string> = {
    green: "border-emerald-300 bg-emerald-50 text-emerald-800",
    red: "border-red-300 bg-red-50 text-red-800",
    gray: "border-slate-300 bg-slate-100 text-slate-700",
    blue: "border-blue-300 bg-blue-50 text-blue-900",
    amber: "border-amber-300 bg-amber-50 text-amber-900",
};

export function HoldChip({
    state,
    amountCents,
    holdOn,
    className,
}: {
    state: HoldChipState;
    amountCents: number | null;
    holdOn?: string | null;
    className?: string;
}) {
    const tone = CHIP_TONE[state];
    const dashed = state === "net30" || state === "wo_vendor";
    return (
        <span
            data-testid="card-hold-chip"
            data-state={state}
            className={cn(
                "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-bold",
                TONE_CLASS[tone],
                dashed && "border-dashed",
                className,
            )}
        >
            <span className="h-2 w-2 rounded-full bg-current" aria-hidden />
            {holdChipLabel(state, amountCents, holdOn)}
        </span>
    );
}

export const CARD_HOLD_HOW_STEPS: HowStep[] = [
    {
        title: "The card gets checked when it's saved",
        text: "The bank says yes or no. No money moves.",
        picture: "card-hold-1-checked.png",
        alt: "The green Card checked line, ringed: the card is real",
    },
    {
        title: "The day before, GWC places a hold",
        text: (
            <>
                At 9:00 AM the day before the visit. Like a hotel hold: the bank sets the money aside. It is <b>not</b>{" "}
                a charge.
            </>
        ),
        picture: "card-hold-2-held.png",
        alt: "The green Held chip, ringed: green means the money is safe",
    },
    {
        title: "Red? Call the customer that day",
        text: "Send a new card link, or tap Collect on site. The job still goes out. GWC tries a card once and never tries it again by itself.",
        picture: "card-hold-3-wont-hold.png",
        alt: "The red Won't hold chip, ringed: call today",
    },
    {
        title: "After the work, Charge takes from the hold",
        text: "Charge the final amount at Close Out. Less than the hold? The rest goes back. More? GWC charges the difference once.",
        picture: "card-hold-4-charge.png",
        alt: "The Charge from hold button, ringed: tap it at Close Out",
    },
    {
        title: "Canceled or moved? Nothing to do",
        text: "GWC lets go of the hold by itself, and holds again the day before the new date.",
        picture: "card-hold-5-released.png",
        alt: "The gray Released chip, ringed: nothing was charged",
    },
];

export function CardHoldHowItWorks({ className }: { className?: string }) {
    return (
        <HowItWorks
            title="How card holds work"
            steps={CARD_HOLD_HOW_STEPS}
            testId="how-card-holds"
            className={className}
        />
    );
}

/** Send new card link / Collect on site — the two answers to a red chip. */
export function useHoldActions(jobId: string, onDone?: () => void) {
    const qc = useQueryClient();
    const refresh = () => {
        void qc.invalidateQueries({ queryKey: ["job-card-hold", jobId] });
        void qc.invalidateQueries({ queryKey: CARD_HOLD_ALERTS_KEY });
        void qc.invalidateQueries({ queryKey: ["book-my-test", "open"] });
        onDone?.();
    };
    const errorText = (e: unknown, fallback: string) => {
        const body = e instanceof ApiError ? (e.body as { message?: string } | null) : null;
        return body?.message ?? fallback;
    };
    const sendLink = useMutation({
        mutationFn: () =>
            api.post<{ sent: { sms: boolean; email: boolean } }>(`/api/jobs/${jobId}/card-hold/send-card-link`, {}),
        onSuccess: (r) => {
            const ch = [r.sent.sms && "text", r.sent.email && "email"].filter(Boolean).join(" and ");
            if (ch) toast.success(`New card link sent by ${ch}. When they save it, GWC holds it right away.`);
            else toast.error("The link was made, but no text or email went out. Check the customer's phone and email.");
            refresh();
        },
        onError: (e) => toast.error(errorText(e, "Couldn't send the card link.")),
    });
    const onSite = useMutation({
        mutationFn: (on: boolean) => api.post<JobHoldView>(`/api/jobs/${jobId}/card-hold/collect-on-site`, { on }),
        onSuccess: (_r, on) => {
            toast.success(
                on ? "Collect on site. The tech takes payment at the visit." : "GWC will hold the card again.",
            );
            refresh();
        },
        onError: (e) => toast.error(errorText(e, "Couldn't save that. Try again.")),
    });
    const release = useMutation({
        mutationFn: () => api.post<JobHoldView>(`/api/jobs/${jobId}/card-hold/release`, {}),
        onSuccess: () => {
            toast.success("Hold let go. Nothing was charged.");
            refresh();
        },
        onError: (e) => toast.error(errorText(e, "Couldn't let the hold go.")),
    });
    return { sendLink, onSite, release, busy: sendLink.isPending || onSite.isPending || release.isPending };
}

const RELEASE_WORDS: Record<string, string> = {
    cancelled: "the job was canceled",
    moved: "the visit moved",
    card_changed: "the customer saved a new card",
    collect_on_site: "switched to collect on site",
    office: "the office let it go",
    paid_another_way: "the invoice was paid another way",
};

function panelText(v: JobHoldView): string {
    const amount = money(v.amountCents);
    const card = cardWords(v.card);
    switch (v.state) {
        case "waiting":
            return `The hold goes on ${dayLabel(v.holdOn)} at 9:00 AM: ${amount} on ${card}. Nothing to do.`;
        case "held":
            return `on ${card}. Placed ${timeLabel(v.placedAt)}. Good until ${timeLabel(v.endsAt)}. Nothing is charged until Close Out.`;
        case "wont_hold":
            return `${v.reason ?? "The bank said no."} We tried once and won't try this card again. Call the customer today.`;
        case "released":
            return `Let go: ${RELEASE_WORDS[v.releaseReason ?? ""] ?? "no longer needed"}. Nothing was charged.`;
        case "expired":
            return `The hold ran out before Close Out. Charge at Close Out will charge ${card} the normal way (one try).`;
        case "charged": {
            const extra = v.extraStatus === "charged" && v.extraCents ? ` plus ${money(v.extraCents)} more` : "";
            const declined =
                v.extraStatus === "declined" && v.extraCents
                    ? ` The extra ${money(v.extraCents)} was declined and is still on the invoice with a pay link.`
                    : "";
            return `Taken from the hold at Close Out: ${money(v.capturedCents)}${extra}. The rest went back.${declined}`;
        }
        case "net30":
            return "Net 30 customers never get a hold. They're invoiced on their terms.";
        case "wo_vendor":
            return "Work-order companies never get a hold.";
        case "collect_on_site":
            return "No hold. The tech takes payment at the visit.";
        case "no_card":
            return "No card on file, so there's nothing to hold. Send the customer a card link.";
        case "no_price":
            return "GWC can't tell the price yet (no quote, no invoice, no devices priced), so it won't hold. Add the devices or a quote.";
        default:
            return "";
    }
}

const PANEL_TONE: Record<Tone, string> = {
    green: "border-emerald-300 bg-emerald-50/80",
    red: "border-red-300 bg-red-50/80",
    gray: "border-border bg-card",
    blue: "border-blue-300 bg-blue-50/80",
    amber: "border-amber-300 bg-amber-50/80",
};

function Timeline({ v }: { v: JobHoldView }) {
    const steps: Array<{ label: string; sub: string; state: "done" | "now" | "todo" }> = [
        { label: "Card checked", sub: "", state: "done" },
        {
            label: "Held",
            sub: v.state === "waiting" ? dayLabel(v.holdOn) : dayOf(v.placedAt),
            state: v.state === "waiting" ? "now" : "done",
        },
        {
            label: "Visit",
            sub: dayLabel(v.visitDate),
            state: v.state === "charged" ? "done" : v.state === "held" ? "now" : "todo",
        },
        { label: "Charged", sub: "at Close Out", state: v.state === "charged" ? "done" : "todo" },
    ];
    return (
        <ol className="grid grid-cols-4 text-[11.5px]" aria-label="Card hold steps">
            {steps.map((s) => (
                <li
                    key={s.label}
                    className={cn(
                        "border-t-4 px-1.5 pt-1.5",
                        s.state === "done" && "border-emerald-600 font-bold text-emerald-800",
                        s.state === "now" && "border-blue-700 font-extrabold text-blue-900",
                        s.state === "todo" && "border-slate-300 text-muted-foreground",
                    )}
                >
                    {s.label}
                    {s.sub && <span className="block font-normal">{s.sub}</span>}
                </li>
            ))}
        </ol>
    );
}

/** The job page's hold chip + panel. Renders nothing when there's nothing to say. */
export function JobCardHoldPanel({ jobId }: { jobId: string }) {
    const q = useQuery({
        queryKey: ["job-card-hold", jobId],
        queryFn: () => api.get<JobHoldView>(`/api/jobs/${jobId}/card-hold`),
        staleTime: 30_000,
        retry: false,
    });
    const acts = useHoldActions(jobId);
    const v = q.data;
    if (!v?.show || !v.state) return null;
    const tone = CHIP_TONE[v.state];
    const chipAmount =
        v.state === "charged"
            ? (v.capturedCents ?? 0) + (v.extraStatus === "charged" ? (v.extraCents ?? 0) : 0)
            : v.amountCents;

    const doRelease = async () => {
        const ok = await confirmDialog({
            title: "Let the hold go?",
            message: `The ${money(v.amountCents)} goes back to the customer. GWC won't hold this visit again. Nothing is charged.`,
            confirmLabel: "Let it go",
        });
        if (ok) acts.release.mutate();
    };

    return (
        <section
            className={cn("space-y-2 rounded-xl border px-3 py-2.5 text-sm", PANEL_TONE[tone])}
            data-testid="job-card-hold"
            data-state={v.state}
            aria-label="Card hold"
        >
            <div className="flex flex-wrap items-center gap-2">
                <span className="flex items-center gap-1.5 font-extrabold">
                    <CreditCard className="h-4 w-4" aria-hidden />
                    Card hold
                </span>
                <HoldChip state={v.state} amountCents={chipAmount} holdOn={v.holdOn} />
                <CardHoldHowItWorks />
            </div>
            {v.state === "wont_hold" && (
                <p className="font-bold text-red-900" data-testid="card-hold-call">
                    Card won&apos;t hold — call the customer
                </p>
            )}
            {v.state === "held" ? (
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                    <span className="text-2xl font-extrabold tabular-nums">{money(v.amountCents)}</span>
                    <span className="min-w-[12rem] flex-1 text-muted-foreground">{panelText(v)}</span>
                </div>
            ) : (
                <p data-testid="card-hold-text">{panelText(v)}</p>
            )}
            {(v.state === "waiting" || v.state === "held" || v.state === "charged") && <Timeline v={v} />}
            <div className="flex flex-wrap gap-2">
                {v.canSendLink && (
                    <Button
                        size="sm"
                        disabled={acts.busy}
                        onClick={() => acts.sendLink.mutate()}
                        data-testid="card-hold-send-link"
                    >
                        {acts.sendLink.isPending ? (
                            <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                        ) : (
                            <Send className="mr-1 h-3.5 w-3.5" />
                        )}
                        Send new card link
                    </Button>
                )}
                {v.canCollectOnSite && (
                    <Button
                        size="sm"
                        variant="outline"
                        className="bg-background"
                        disabled={acts.busy}
                        onClick={() => acts.onSite.mutate(true)}
                        data-testid="card-hold-collect-on-site"
                    >
                        <MapPin className="mr-1 h-3.5 w-3.5" />
                        {v.state === "wont_hold" ? "Collect on site" : "Switch to collect on site"}
                    </Button>
                )}
                {v.canRelease && (
                    <Button
                        size="sm"
                        variant="outline"
                        className="bg-background"
                        disabled={acts.busy}
                        onClick={() => void doRelease()}
                        data-testid="card-hold-release"
                    >
                        <Unlock className="mr-1 h-3.5 w-3.5" />
                        Release hold
                    </Button>
                )}
                {v.state === "collect_on_site" && (
                    <Button
                        size="sm"
                        variant="outline"
                        className="bg-background"
                        disabled={acts.busy}
                        onClick={() => acts.onSite.mutate(false)}
                        data-testid="card-hold-undo-on-site"
                    >
                        <Undo2 className="mr-1 h-3.5 w-3.5" />
                        Hold the card instead
                    </Button>
                )}
            </div>
        </section>
    );
}

/** One red "Card won't hold — call the customer" row, with its two buttons. */
export function CardHoldCallRow({ alert, onOpenJob }: { alert: CardHoldAlert; onOpenJob?: (jobId: string) => void }) {
    const acts = useHoldActions(alert.jobId);
    const digits = (alert.phone ?? "").replace(/[^\d+]/g, "");
    return (
        <article
            className="space-y-1.5 rounded-lg border border-red-300 border-l-4 border-l-red-700 bg-card px-3 py-2 text-[13px] text-foreground"
            data-testid="card-hold-call-row"
        >
            <b className="block text-red-800">Card won&apos;t hold — call the customer</b>
            <span className="block">
                {onOpenJob ? (
                    <button type="button" className="font-semibold underline" onClick={() => onOpenJob(alert.jobId)}>
                        {alert.customerName}
                    </button>
                ) : (
                    <span className="font-semibold">{alert.customerName}</span>
                )}
                {alert.devices > 0 && ` · ${alert.devices} device${alert.devices === 1 ? "" : "s"}`} ·{" "}
                <b>{alert.when}</b>
            </span>
            <span className="block text-muted-foreground">
                Bank said: {alert.reason} We tried once and won&apos;t try again.
            </span>
            {alert.linkSentAt && (
                <span className="block font-semibold text-emerald-800">
                    New card link sent. Waiting on the customer.
                </span>
            )}
            {alert.phone && (
                <a
                    className="inline-flex items-center gap-1 text-muted-foreground"
                    href={digits ? `tel:${digits}` : undefined}
                >
                    <Phone className="h-3.5 w-3.5" aria-hidden /> {alert.phone}
                </a>
            )}
            <div className="flex flex-wrap gap-1.5 pt-0.5">
                <Button
                    size="sm"
                    disabled={acts.busy}
                    onClick={() => acts.sendLink.mutate()}
                    data-testid="call-row-send-link"
                >
                    {acts.sendLink.isPending ? (
                        <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                    ) : (
                        <Send className="mr-1 h-3.5 w-3.5" />
                    )}
                    Send new card link
                </Button>
                <Button
                    size="sm"
                    variant="outline"
                    className="bg-background"
                    disabled={acts.busy}
                    onClick={() => acts.onSite.mutate(true)}
                    data-testid="call-row-collect-on-site"
                >
                    <MapPin className="mr-1 h-3.5 w-3.5" />
                    Collect on site
                </Button>
            </div>
        </article>
    );
}

/** Closeout's strip: the same red rows as the booking chip. Nothing when there are none. */
export function CardHoldCallStrip({ onOpenJob }: { onOpenJob?: (jobId: string) => void }) {
    const q = useQuery({
        queryKey: CARD_HOLD_ALERTS_KEY,
        queryFn: () => api.get<{ alerts: CardHoldAlert[]; count: number }>("/api/card-holds/alerts"),
        refetchInterval: 60_000,
        staleTime: 30_000,
        retry: false,
    });
    const alerts = q.data?.alerts ?? [];
    if (alerts.length === 0) return null;
    return (
        <section className="space-y-2 rounded-xl border bg-card p-3" data-testid="card-hold-call-strip">
            <div className="flex items-center gap-2">
                <b className="text-sm">
                    {alerts.length} card{alerts.length === 1 ? "" : "s"} won&apos;t hold
                </b>
                <span className="rounded-md bg-red-50 px-1.5 py-0.5 text-[11.5px] font-bold text-red-800">
                    Waiting on: office to call
                </span>
                <CardHoldHowItWorks />
            </div>
            <div className="grid gap-2 md:grid-cols-2">
                {alerts.map((a) => (
                    <CardHoldCallRow key={a.jobId} alert={a} onOpenJob={onOpenJob} />
                ))}
            </div>
        </section>
    );
}

/* ═══════════════════════ Step 5: Charge from the hold ═══════════════════════ */

/** GET charge-guard `hold` / Close Out payment `cardHold` (cardHoldView.ts holdForCharge). */
export interface ChargeHold {
    state: "held" | "expired";
    amountCents: number | null;
    last4: string | null;
    endsAt: string | null;
}

/** POST /invoices/:id/charge-card → `fromHold` (invoiceAutoCharge.ts FromHold). */
export interface FromHold {
    holdId: string;
    heldAmount: number;
    capturedAmount: number;
    releasedAmount: number;
    extraAmount: number;
    extraStatus: "none" | "charged" | "declined" | "skipped";
    extraReason: string | null;
}

/**
 * The Charge button's words and the line under it, when the job has a hold.
 * Pure (mockup moment 4): less than the hold → "Charge $112.40 from hold";
 * more → "Charge $143.69" (the whole hold + one charge for the rest); ran out
 * → the normal charge, said so.
 */
export function holdChargeWords(
    balanceCents: number | null,
    hold: ChargeHold | null | undefined,
): { button: string | null; note: string | null } {
    if (!hold) return { button: null, note: null };
    if (hold.state === "expired")
        return { button: null, note: "The hold ran out. This charges the card the normal way (one try)." };
    if (balanceCents == null || hold.amountCents == null)
        return { button: "Charge from hold", note: "Takes the final amount from the hold on the card." };
    const bal = money(balanceCents);
    if (balanceCents <= hold.amountCents) {
        const back = hold.amountCents - balanceCents;
        return {
            button: `Charge ${bal} from hold`,
            note:
                back > 0
                    ? `Takes ${bal} from the hold. The other ${money(back)} goes back to the customer on its own.`
                    : `Takes ${bal} from the hold.`,
        };
    }
    return {
        button: `Charge ${bal}`,
        note: `Takes the full ${money(hold.amountCents)} hold, then charges ${money(balanceCents - hold.amountCents)} more for the extra work. One receipt.`,
    };
}

/** The toast after a charge that came from a hold. */
export function fromHoldMessage(f: FromHold): { ok: boolean; text: string } {
    const dollars = (n: number) => `$${n.toFixed(2)}`;
    if (f.extraStatus === "declined" || f.extraStatus === "skipped") {
        return {
            ok: false,
            text: `Took ${dollars(f.capturedAmount)} from the hold. The extra ${dollars(f.extraAmount)} was declined${f.extraReason ? ` (${f.extraReason.replace(/\.$/, "")})` : ""}. It stays on the invoice with a pay link.`,
        };
    }
    if (f.extraStatus === "charged")
        return {
            ok: true,
            text: `Took the ${dollars(f.capturedAmount)} hold and charged ${dollars(f.extraAmount)} more. Paid in full.`,
        };
    return {
        ok: true,
        text:
            f.releasedAmount > 0
                ? `Charged ${dollars(f.capturedAmount)} from the hold. The other ${dollars(f.releasedAmount)} went back to the customer.`
                : `Charged ${dollars(f.capturedAmount)} from the hold.`,
    };
}
